<script setup>
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useLoading } from "vue-loading-overlay";
import { computed, onMounted, onUnmounted, ref } from "vue";
import moment from "moment";
import { errorNotify, successNotify } from "@/mixins";
import DataTable from "@/components/panels/DataTable.vue";
import RowActions from "@/components/panels/RowActions.vue";
import PaginationBar from "@/components/layouts/PaginationBar.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import FormModal from "@/components/modals/FormModal.vue";

const store = useStore();
const router = useRouter();
const $loading = useLoading({});
const timer = setInterval(update, 60000);

const lockerList = computed(() => store.getters['locker/lockerList']);
const modelList = computed(() => store.getters['model/modelList']);
const organizationList = computed(() => store.getters['organization/organizationList']);
const lockersStatistic = computed(() => store.getters['statistic/lockersStatistic']);
const currentUserData = computed(() => store.getters['user/currentUserData']);
const cityList = computed(() => store.getters['city/cityList']);
const addressList = computed(() => store.getters['address/addressList']);
const controlUnitList = computed(() => store.getters['control_unit/controlUnitList']);
const cellFirmwareList = computed(() => store.getters['firmware/cellFirmwareList']);
const headFirmwareList = computed(() => store.getters['firmware/headFirmwareList']);
const lockerCreateError = computed(() => store.getters['locker/lockerCreateError']);
const lockerDeleteError = computed(() => store.getters['locker/lockerDeleteError']);

const lockers = computed(() => lockerList.value?.results || []);
const isAdmin = computed(() => currentUserData.value.role === 'administrator');

const page = ref(1);
const searchName = ref('');
const status = ref('');
const organization = ref('');
const model = ref('');

const STATUS_META = {
  online: { pill: 'online', label: 'Online' },
  offline: { pill: 'offline', label: 'Offline' },
  maintenance: { pill: 'maint', label: 'Maintenance' },
  error: { pill: 'error', label: 'Error' },
  sleeping: { pill: 'offline', label: 'Sleeping' },
};

// Backend caps page_size at 100; the pickers below show at most that many options.
const MAX_PAGE_SIZE = 100;

function getFilteredLockerList() {
  let filter = `?page=${page.value}`;
  if (organization.value) filter += `&organization=${organization.value}`;
  if (model.value) filter += `&model=${model.value}`;
  if (status.value) filter += `&status=${status.value}`;
  if (searchName.value) filter += `&name=${searchName.value}`;
  return store.dispatch('locker/getLockerList', filter);
}

function applyFilters() {
  page.value = 1;
  getFilteredLockerList();
}

function update() {
  store.dispatch('statistic/getLockersStatistic');
  getFilteredLockerList();
  store.dispatch('organization/getOrganizationList', `?page_size=${MAX_PAGE_SIZE}`);
  store.dispatch('model/getModelList', `?page_size=${MAX_PAGE_SIZE}`);
}

onMounted(() => {
  const loader = $loading.show({});
  update();
  loader.hide();
});
onUnmounted(() => clearInterval(timer));

const stat = computed(() => lockersStatistic.value || {});

const cellMetrics = computed(() => [
  { label: 'Active', value: stat.value.active_cells ?? 0, icon: 'shield-check-outline', color: 'var(--lb-success)' },
  { label: 'Broken', value: stat.value.broken_cells ?? 0, icon: 'link-variant-off', color: 'var(--lb-red)' },
  { label: 'Occupied', value: stat.value.occupied_cells ?? 0, icon: 'package-variant-closed', color: 'var(--lb-blue)' },
  { label: 'Cleaning', value: stat.value.cleaning_required_cells ?? 0, icon: 'broom', color: 'var(--lb-warning)' },
]);

const lockerMetrics = computed(() => [
  { label: 'Online', value: stat.value.online_lockers ?? 0, icon: 'access-point', color: 'var(--lb-success)' },
  { label: 'Offline', value: stat.value.offline_lockers ?? 0, icon: 'web-off', color: 'var(--lb-slate)' },
  { label: 'Maintenance', value: stat.value.maintenance_lockers ?? 0, icon: 'wrench-outline', color: 'var(--lb-warning)' },
  { label: 'Error', value: stat.value.error_lockers ?? 0, icon: 'alert-outline', color: 'var(--lb-red)' },
]);

const syncAgo = (ts) => (ts ? moment(ts).fromNow() : 'Never');

function goToDetail(item) {
  router.push({ name: 'locker', params: { slug: item.sn } });
}

// ---- add ----
const adding = ref(false);
const submitting = ref(false);

const opts = (list, value, label) => (list?.results || []).map((x) => ({ value: x[value], label: x[label] }));

const lockerFields = computed(() => [
  { key: 'organization', label: 'Organization', type: 'select', required: true, full: true,
    options: opts(organizationList.value, 'id', 'name') },
  { key: 'name', label: 'Name', placeholder: 'e.g. Central Station #1', required: true },
  { key: 'sn', label: 'Serial number', placeholder: 'Generated if empty', optional: true },
  { key: 'model', label: 'Model', type: 'select', required: true,
    options: opts(modelList.value, 'code', 'name') },
  { key: 'control_unit', label: 'Control unit', type: 'select', required: true,
    // a unit already installed in a device can't be reused (OneToOne on the backend)
    options: (controlUnitList.value?.results || []).filter((u) => !u.device).map((u) => u.sn),
    placeholder: 'Select a free unit…' },
  { key: 'head_firmware_version', label: 'Head firmware', type: 'select', required: true,
    options: opts(headFirmwareList.value, 'id', 'version') },
  { key: 'cell_firmware_version', label: 'Cell firmware', type: 'select', required: true,
    options: opts(cellFirmwareList.value, 'id', 'version') },
  { key: 'city', label: 'City', type: 'select', required: true,
    options: opts(cityList.value, 'id', 'name') },
  { key: 'address', label: 'Address', type: 'select', required: true, dependsOn: 'city',
    options: opts(addressList.value, 'id', 'line1'), placeholderDisabled: 'Select a city first…' },
  { key: 'ip_address', label: 'IP address', placeholder: '10.252.1.1', optional: true, full: true },
  { key: 'wifi_ssid', label: 'Wi-Fi SSID', optional: true, autocomplete: 'off' },
  { key: 'wifi_password', label: 'Wi-Fi password', type: 'password', optional: true, autocomplete: 'new-password' },
]);

function openAddModal() {
  const size = `?page_size=${MAX_PAGE_SIZE}`;
  store.dispatch('organization/getOrganizationList', size);
  store.dispatch('model/getModelList', size);
  store.dispatch('control_unit/getControlUnitList', size);
  store.dispatch('firmware/getHeadFirmwareList', size);
  store.dispatch('firmware/getCellFirmwareList', size);
  store.dispatch('city/getCityList', size);
  adding.value = true;
}

function onLockerFieldChange(key, value) {
  if (key === 'city' && value) {
    store.dispatch('address/getAddressList', `?city=${value}&page_size=${MAX_PAGE_SIZE}`);
  }
}

function createLocker(vals) {
  // eslint-disable-next-line no-unused-vars
  const { city, ...data } = vals;
  // optional fields: omit when blank, so the backend generates sn and leaves ip null
  ['sn', 'ip_address'].forEach((k) => {
    data[k] = (data[k] || '').trim();
    if (!data[k]) delete data[k];
  });
  // Wi-Fi credentials are sent verbatim (spaces can be part of them), only dropped when empty
  ['wifi_ssid', 'wifi_password'].forEach((k) => {
    if (!data[k]) delete data[k];
  });
  submitting.value = true;
  store.dispatch('locker/createLocker', data)
    .then(() => {
      if (lockerCreateError.value) {
        errorNotify(lockerCreateError.value);
        return;
      }
      successNotify('Locker added');
      adding.value = false;
      getFilteredLockerList();
      store.dispatch('statistic/getLockersStatistic');
    })
    .finally(() => { submitting.value = false; });
}

// ---- delete ----
const del = ref(null);
function confirmDelete() {
  store.dispatch('locker/deleteLocker', del.value.sn)
    .then(() => {
      if (lockerDeleteError.value) {
        errorNotify(lockerDeleteError.value);
        return;
      }
      successNotify('Locker deleted');
      getFilteredLockerList();
      store.dispatch('statistic/getLockersStatistic');
    })
    .finally(() => { del.value = null; });
}

const cols = 'minmax(190px,1.4fr) 110px minmax(210px,1fr) minmax(190px,1.2fr) 130px 150px 110px';
const columns = ['Locker', 'Model', 'Slots', 'Location', 'Status', 'Organization', 'Sync'];
</script>

<template>
  <Teleport to="#topbar-actions">
    <button v-if="isAdmin" class="btn-primary" type="button" @click="openAddModal">
      <span class="mdi mdi-plus"></span>
      Add Locker
    </button>
  </Teleport>

  <!-- metrics -->
  <div class="lkmetrics-row">
    <div class="metric-cluster">
      <div class="mc-total">
        <span class="mc-total__wm mdi mdi-view-grid-outline"></span>
        <div class="mc-total__title">Total Cells</div>
        <div class="mc-total__num">{{ (stat.cells ?? 0).toLocaleString() }}</div>
      </div>
      <div v-for="m in cellMetrics" :key="m.label" class="mcard" :class="{ 'is-zero': m.value === 0 }">
        <div class="mcard__l">
          <div class="mcard__ico"><span class="mdi" :class="`mdi-${m.icon}`" :style="{ color: m.color }"></span></div>
          <div class="mcard__label">{{ m.label }}</div>
        </div>
        <div class="mcard__num">{{ m.value }}</div>
      </div>
    </div>
    <div class="metric-cluster">
      <div class="mc-total">
        <span class="mc-total__wm mdi mdi-locker-multiple"></span>
        <div class="mc-total__title">Total Lockers</div>
        <div class="mc-total__num">{{ (stat.lockers ?? 0).toLocaleString() }}</div>
      </div>
      <div v-for="m in lockerMetrics" :key="m.label" class="mcard" :class="{ 'is-zero': m.value === 0 }">
        <div class="mcard__l">
          <div class="mcard__ico"><span class="mdi" :class="`mdi-${m.icon}`" :style="{ color: m.color }"></span></div>
          <div class="mcard__label">{{ m.label }}</div>
        </div>
        <div class="mcard__num">{{ m.value }}</div>
      </div>
    </div>
  </div>

  <!-- toolbar -->
  <!-- TODO(city filter): the design expects an "All cities" filter, but the device/ list
       endpoint doesn't accept city / address__city / address__city__id / city_id /
       location — they're all silently ignored (200, count unchanged), unlike
       organization/model/address/status, which do validate (400 on a bad id).
       Either add city to the backend filterset, or fetch lockers wholesale
       (?page_size=1000, like DevicesLogs.vue) and filter client-side — that
       disables server-side pagination on this page. -->
  <div class="toolbar">
    <div class="field-search">
      <span class="mdi mdi-magnify"></span>
      <input v-model="searchName" placeholder="Search by name or S/N…" @input="applyFilters">
    </div>
    <div class="field-select">
      <span class="mdi mdi-access-point lead"></span>
      <select v-model="status" @change="applyFilters">
        <option value="">All statuses</option>
        <option v-for="(meta, key) in STATUS_META" :key="key" :value="key">{{ meta.label }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div v-if="isAdmin && organizationList" class="field-select">
      <span class="mdi mdi-office-building-outline lead"></span>
      <select v-model="organization" @change="applyFilters">
        <option value="">All organizations</option>
        <option v-for="o in organizationList.results || []" :key="o.id" :value="o.id">{{ o.name }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div v-if="modelList" class="field-select">
      <span class="mdi mdi-cube-outline lead"></span>
      <select v-model="model" @change="applyFilters">
        <option value="">All models</option>
        <option v-for="m in modelList.results || []" :key="m.code" :value="m.code">{{ m.name }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="spacer"></div>
    <span class="count">{{ lockerList?.count ?? 0 }} lockers</span>
  </div>

  <!-- table -->
  <DataTable :cols="cols" :columns="columns" :rows="lockers" row-key="sn" empty-text="No lockers match your filters.">
    <template #row="{ row }">
      <div class="td" style="cursor: pointer" @click="goToDetail(row)">
        <div class="td-name">
          <div class="ic"><span class="mdi mdi-locker-multiple"></span></div>
          <div class="nm">
            <b>{{ row.name }}</b>
            <small>{{ row.sn }}</small>
          </div>
        </div>
      </div>
      <div class="td"><span>{{ row.model?.code }}</span></div>
      <div class="td">
        <div class="slots">
          <span class="s active" :class="{ zero: !row.slots?.active }" title="Active">
            <span class="mdi mdi-shield-check-outline"></span>{{ row.slots?.active ?? 0 }}
          </span>
          <span class="s broken" :class="{ zero: !row.slots?.broken }" title="Broken">
            <span class="mdi mdi-link-variant-off"></span>{{ row.slots?.broken ?? 0 }}
          </span>
          <span class="s occupied" :class="{ zero: !row.slots?.occupied }" title="Occupied">
            <span class="mdi mdi-package-variant-closed"></span>{{ row.slots?.occupied ?? 0 }}
          </span>
          <span class="s reserved" :class="{ zero: !row.slots?.cleaning_required }" title="Cleaning required">
            <span class="mdi mdi-broom"></span>{{ row.slots?.cleaning_required ?? 0 }}
          </span>
        </div>
      </div>
      <div class="td">
        <div class="loc">
          <b>{{ row.address?.line1 }}</b>
          <small><span class="mdi mdi-map-marker-outline"></span>{{ row.address?.city?.name }}</small>
        </div>
      </div>
      <div class="td">
        <span class="pill" :class="`pill--${(STATUS_META[row.status] || STATUS_META.offline).pill}`">
          <span class="d"></span>
          {{ (STATUS_META[row.status] || { label: row.status }).label }}
        </span>
      </div>
      <div class="td"><span>{{ row.organization?.name }}</span></div>
      <div class="td"><span class="muted">{{ syncAgo(row.last_sync) }}</span></div>
      <RowActions
        :copies="[
          { icon: 'content-copy', label: 'Copy S/N', text: row.sn },
          { icon: 'map-marker-outline', label: 'Copy address', text: row.address?.line1 || '' },
        ]"
        detail-label="Open details"
        :deletable="isAdmin"
        @detail="goToDetail(row)"
        @delete="del = row" />
    </template>
  </DataTable>

  <PaginationBar
    :page="page"
    :total-pages="lockerList?.total_pages || 1"
    :count="lockerList?.count || 0"
    @update:page="(p) => { page = p; getFilteredLockerList(); }" />

  <FormModal
    :open="adding"
    title="Add Locker"
    :fields="lockerFields"
    submit-label="Add locker"
    :submitting="submitting"
    @change="onLockerFieldChange"
    @submit="createLocker"
    @cancel="adding = false" />

  <ConfirmModal
    :open="!!del"
    danger
    title="Delete locker?"
    :message="del ? `“${del.name}” (${del.sn}) will be permanently removed.` : ''"
    confirm-label="Delete"
    @confirm="confirmDelete"
    @cancel="del = null" />
</template>
